import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { Box, Button, Chip, Stack, Typography, alpha } from "@mui/material";

import sncrLogo from "../assets/sncr-govbr-logo.png";

function SncrMark({ size = 22 }: { size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 0.5,
        overflow: "hidden",
        bgcolor: "common.white",
      }}
    >
      <Box
        component="img"
        src={sncrLogo}
        alt=""
        sx={{
          height: size,
          width: "auto",
          maxWidth: "none",
          display: "block",
          objectFit: "cover",
          objectPosition: "left center",
          transform: "scale(1.05)",
          transformOrigin: "left center",
        }}
      />
    </Box>
  );
}

type SncrConnectButtonProps = {
  isConnected: boolean;
  onConnect: () => void;
  onConnectedClick?: () => void;
  fullWidth?: boolean;
};

export function SncrConnectButton({
  isConnected,
  onConnect,
  onConnectedClick,
  fullWidth = false,
}: SncrConnectButtonProps) {
  if (isConnected) {
    const clickable = Boolean(onConnectedClick);

    return (
      <Button
        fullWidth={fullWidth}
        variant="outlined"
        disableRipple={!clickable}
        onClick={onConnectedClick}
        sx={(theme) => ({
          textTransform: "none",
          flexShrink: 0,
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          justifyContent: fullWidth ? "flex-start" : "center",
          borderColor: alpha(theme.palette.success.main, 0.5),
          bgcolor: "common.white",
          color: "text.primary",
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
          pointerEvents: clickable ? "auto" : "none",
          cursor: clickable ? "pointer" : "default",
          "&:hover": {
            borderColor: clickable
              ? theme.palette.success.main
              : alpha(theme.palette.success.main, 0.5),
            bgcolor: "common.white",
            boxShadow: clickable
              ? "0 2px 8px rgba(16, 24, 40, 0.10)"
              : "0 1px 2px rgba(16, 24, 40, 0.06)",
          },
        })}
        aria-label={
          clickable
            ? "SNCR conectado — abrir gestão de numerações"
            : "Conectado ao SNCR"
        }
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <SncrMark />
          <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
            SNCR
          </Typography>
          <Chip
            size="small"
            icon={<CheckCircleRounded sx={{ fontSize: "16px !important" }} />}
            label="Ativo"
            color="success"
            sx={{
              height: 22,
              fontWeight: 700,
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
        </Stack>
      </Button>
    );
  }

  return (
    <Button
      fullWidth={fullWidth}
      variant="outlined"
      onClick={onConnect}
      sx={(theme) => ({
        textTransform: "none",
        flexShrink: 0,
        minHeight: 40,
        px: 1.5,
        py: 0.75,
        borderRadius: 1.5,
        justifyContent: fullWidth ? "flex-start" : "center",
        borderColor: "divider",
        bgcolor: "common.white",
        color: "text.primary",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
        transition:
          "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: "common.white",
          boxShadow: "0 2px 8px rgba(16, 24, 40, 0.10)",
        },
      })}
      aria-label="Conectar ao SNCR"
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <SncrMark />
        <Box
          sx={(theme) => ({
            width: "1px",
            alignSelf: "stretch",
            bgcolor: theme.palette.divider,
            my: 0.25,
          })}
        />
        <Stack spacing={0} alignItems="flex-start">
          <Typography
            variant="body2"
            fontWeight={600}
            lineHeight={1.15}
            sx={(theme) => ({ fontSize: theme.typography.body2.fontSize })}
          >
            Conectar ao SNCR
          </Typography>
          <Typography
            variant="caption"
            lineHeight={1.1}
            sx={(theme) => ({
              fontSize: theme.typography.caption.fontSize,
              color: theme.palette.text.secondary,
            })}
          >
            Autenticação Gov.br
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );
}
